<template>
  <base-section
    id="service-features"
    :background="background"
    space="30"
  >
    <base-section-heading
      :title="$t('products.title')"
      :mobile-size="'text-h5'"
      :is-uppercase="false"
      title-tag="h2"
      :space="isMobile ? 2 : 6"
    />
    <v-container style="max-width: 1160px">
      <!-- 移动端：原生横向滚动 tab 栏 -->
      <div
        v-if="isMobile && !loading"
        class="native-tab-bar"
      >
        <button
          v-for="(t, i) in tabs"
          :key="i"
          class="native-tab-item"
          :class="{ 'native-tab-item--active': tab === i }"
          @click="tab = i"
        >
          {{ t }}
        </button>
      </div>
      <!-- 桌面端：Vuetify v-tabs -->
      <v-tabs
        v-if="!isMobile && !loading"
        :key="$i18n.locale"
        v-model="tab"
        centered
        show-arrows
        class="mb-8"
      >
        <v-tab
          v-for="(t, i) in tabs"
          :key="i"
          :value="i"
          class="text-capitalize"
        >
          {{ t }}
        </v-tab>
      </v-tabs>
      <v-row v-if="loading">
        <v-col
          cols="12"
          class="text-center"
        >
          <v-progress-circular
            indeterminate
            color="primary"
            size="64"
          />
          <p class="mt-4">
            {{ $t('common.loading') }}
          </p>
        </v-col>
      </v-row>
      <v-row v-else>
        <v-col
          v-for="card in filteredCards"
          :key="card.id"
          class="card-col gallery-card"
          cols="12"
          sm="6"
          md="4"
        >
          <v-fade-transition>
            <product-card
              :src="card.src"
              :title="card.title"
              :desc="card.desc"
              :item-id="card.itemId"
              :clickable="card.clickable"
            />
          </v-fade-transition>
        </v-col>
      </v-row>
    </v-container>
  </base-section>
</template>

<script>
  import { loadImage } from '@/utils/imageLoader'
  import productsData from '@/data/products.json'
  import ProductCard from '@/components/ProductCard'

  export default {
    name: 'ProductCenter',

    components: {
      ProductCard,
    },

    data: () => ({
      background: require('@/assets/bg-w-1.png'),
      cards: [],
      loading: true,
      tab: 0,
      categoryOptions: [],
    }),

    computed: {
      isMobile() {
        return this.$vuetify.breakpoint.width <= 768
      },

      tabs() {
        return [this.$t('products.gallery.all'), ...this.categoryOptions.map(item => item.label)]
      },

      activeCategoryValue() {
        if (this.tab <= 0) return ''
        const option = this.categoryOptions[this.tab - 1]
        return option?.value || ''
      },

      filteredCards() {
        return this.cards.filter(card => {
          if (!this.activeCategoryValue) return true
          return card.category === this.activeCategoryValue
        })
      },
    },

    watch: {
      '$i18n.locale'() {
        this.loadCards()
      },
    },

    async mounted() {
      await this.loadCards()
    },

    methods: {
      async loadCategoryOptions() {
        const locale = (this.$i18n.locale || 'en').toLowerCase()
        let list = []
        try {
          const resp = await this.$axios({
            method: 'get',
            url: '/public/dict/data',
            params: { dictType: 'product_category' },
          })
          const active = Array.isArray(resp) ? resp.filter(item => item?.status === '0') : []
          if (active.length > 0) list = active
        } catch {}

        if (!Array.isArray(list) || list.length === 0) {
          this.categoryOptions = [
            { label: locale.startsWith('zh') ? '无人机' : 'Drone', value: 'Drone' },
            { label: locale.startsWith('zh') ? '抗干扰' : 'Anti-Jamming', value: 'Anti-Jamming' },
            { label: locale.startsWith('zh') ? '成像' : 'Imaging', value: 'Imaging' },
            { label: locale.startsWith('zh') ? '反无人机' : 'Anti-Drone', value: 'Anti-Drone' },
            { label: locale.startsWith('zh') ? '无GNSS' : 'GNSS-Free', value: 'GNSS-Free' },
          ]
        } else {
          this.categoryOptions = list.map(item => ({
            label: item.dictLabel || item.dictValue,
            value: item.dictValue,
          }))
        }

        if (this.tab >= this.tabs.length) {
          this.tab = 0
        }
      },

      translateIfKey(value) {
        if (typeof value !== 'string') return value
        return this.$te(value) ? this.$t(value) : value
      },
      async loadCards() {
        this.loading = true
        try {
          await this.loadCategoryOptions()
          const lang = this.$i18n.locale
          const resp = await this.$axios({
            method: 'get',
            url: '/public/products',
            params: { lang },
          })
          const cards = await this.buildCardsFromApi(resp)
          this.cards = cards.length ? cards : await this.buildCardsFromLocal()
        } catch (error) {
          console.error('Failed to load cards:', error)
          this.cards = await this.buildCardsFromLocal()
        } finally {
          this.loading = false
        }
      },

      async buildCardsFromApi(products) {
        if (!Array.isArray(products) || !products.length) {
          return []
        }
        const cards = []
        const sortedProducts = [...products]
          .filter(product => product && product.visible !== false)
          .sort((a, b) => (Number(a?.sortOrder || 0) - Number(b?.sortOrder || 0)) || (Number(b?.id || 0) - Number(a?.id || 0)))
        for (const product of sortedProducts) {
          const imgUrl = await loadImage(product.coverImage || require('@/assets/logo/logo.png'))
          cards.push({
            id: product.id,
            title: product.name,
            desc: product.desc || product.summary || '',
            src: imgUrl,
            category: product.category,
            itemId: product.slug || String(product.id),
            clickable: !!(product.id && product.clickable !== false),
          })
        }
        return cards
      },

      async buildCardsFromLocal() {
        const items = Array.isArray(productsData?.products) ? productsData.products : []
        const cards = []
        for (const item of items) {
          const imgUrl = await loadImage(item.imgUrl || 'logo/logo.png')
          const title = this.translateIfKey(item.title)
          const desc = this.translateIfKey(item.desc)
          cards.push({
            id: item.id,
            title,
            desc,
            src: imgUrl,
            category: item.category,
            itemId: item.slug || String(item.id),
            clickable: !!(item.id),
          })
        }
        return cards
      },
    },
  }
</script>
<style lang="sass" scoped>
.card-col
  // background: green
.card-box
  height: 100%
  // background: red
</style>
